import { useCallback, useEffect, useRef, useState } from 'react';
import { listStoreBrandTargets, listStoreTargets } from '../../../services/configTemplateService';
import type {
    ConfigTemplateLevel,
    ConfigTemplateTargetBrand,
    ConfigTemplateTargetFilters,
    ConfigTemplateTargetStore,
} from '../../../types/configTemplate.types';

export type TargetItem = ConfigTemplateTargetBrand | ConfigTemplateTargetStore;

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 25;

function buildFilters(args: {
    search: string;
    stateFilters: string[];
    brandFilters: string[];
    page: number;
    limit: number;
}): ConfigTemplateTargetFilters {
    const filters: ConfigTemplateTargetFilters = {
        search: args.search.trim() || undefined,
        page: args.page,
        limit: args.limit,
    };
    if (args.stateFilters.length === 1) {
        filters.state = args.stateFilters[0];
    } else if (args.stateFilters.length > 1) {
        filters.states = args.stateFilters;
    }
    if (args.brandFilters.length === 1) {
        filters.storeBrandId = args.brandFilters[0];
    } else if (args.brandFilters.length > 1) {
        filters.storeBrandIds = args.brandFilters;
    }
    return filters;
}

export function useTargetPicker(args: {
    level: ConfigTemplateLevel;
    mode: 'copy' | 'apply';
    defaultState?: string;
    open: boolean;
}) {
    const minChars = 2;
    const initialStates = args.defaultState ? [args.defaultState] : [];
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [stateFilters, setStateFilters] = useState<string[]>(initialStates);
    const [brandFilters, setBrandFilters] = useState<string[]>([]);
    const [items, setItems] = useState<TargetItem[]>([]);
    const [total, setTotal] = useState(0);
    const [catalogTotal, setCatalogTotal] = useState<number | null>(null);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [catalogStatus, setCatalogStatus] = useState<'idle' | 'loading' | 'success' | 'error'>(
        'idle'
    );
    const [error, setError] = useState<string | null>(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [moreError, setMoreError] = useState(false);
    const [selected, setSelected] = useState<Record<string, TargetItem>>({});
    const [allMatching, setAllMatching] = useState(false);
    const [excluded, setExcluded] = useState<Record<string, true>>({});
    const [brandOptions, setBrandOptions] = useState<ConfigTemplateTargetBrand[]>([]);
    const [brandOptionsStatus, setBrandOptionsStatus] = useState<
        'idle' | 'loading' | 'success' | 'error'
    >('idle');
    const [brandOptionInput, setBrandOptionInput] = useState('');
    const [brandOptionSearch, setBrandOptionSearch] = useState('');
    const timer = useRef<number | null>(null);
    const brandTimer = useRef<number | null>(null);
    const requestId = useRef(0);

    const gated = search.trim().length < minChars;
    const waitingSearch = searchInput.trim() !== search.trim() && !gated;
    const hasFilters =
        search.trim().length > 0 || stateFilters.length > 0 || brandFilters.length > 0;

    const fetchPage = useCallback(
        async (nextPage: number, append: boolean) => {
            if (search.trim().length < minChars) {
                setItems([]);
                setTotal(0);
                setStatus('idle');
                return;
            }
            const id = ++requestId.current;
            if (append) {
                setLoadingMore(true);
                setMoreError(false);
            } else {
                setStatus('loading');
                setError(null);
                setMoreError(false);
            }
            try {
                const filters = buildFilters({
                    search,
                    stateFilters,
                    brandFilters: args.level === 'STORE' ? brandFilters : [],
                    page: nextPage,
                    limit: PAGE_SIZE,
                });
                const data =
                    args.level === 'STORE_BRAND'
                        ? await listStoreBrandTargets(filters)
                        : await listStoreTargets(filters);
                if (id !== requestId.current) return;
                setItems((prev) => (append ? [...prev, ...data.items] : data.items));
                setTotal(data.total);
                setPage(nextPage);
                setStatus('success');
                setLoadingMore(false);
            } catch {
                if (id !== requestId.current) return;
                if (append) {
                    setLoadingMore(false);
                    setMoreError(true);
                    return;
                }
                setError(null);
                setStatus('error');
            }
        },
        [args.level, brandFilters, minChars, search, stateFilters]
    );

    const probeCatalog = useCallback(async () => {
        setCatalogStatus('loading');
        try {
            const data =
                args.level === 'STORE_BRAND'
                    ? await listStoreBrandTargets({ page: 1, limit: 1 })
                    : await listStoreTargets({ page: 1, limit: 1 });
            setCatalogTotal(data.total);
            setCatalogStatus('success');
        } catch {
            setError(null);
            setCatalogStatus('error');
        }
    }, [args.level]);

    useEffect(() => {
        if (!args.open) return;
        setSearchInput('');
        setSearch('');
        setStateFilters(args.defaultState ? [args.defaultState] : []);
        setBrandFilters([]);
        setItems([]);
        setSelected({});
        setAllMatching(false);
        setExcluded({});
        setStatus('idle');
        setTotal(0);
        setCatalogTotal(null);
        setCatalogStatus('loading');
        setError(null);
        setLoadingMore(false);
        setMoreError(false);
        setBrandOptions([]);
        setBrandOptionsStatus('idle');
        setBrandOptionInput('');
        setBrandOptionSearch('');
        void probeCatalog();
        // reset only when the surface opens
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [args.open]);

    useEffect(() => {
        if (!args.open) return;
        if (search.trim().length < minChars) {
            setItems([]);
            setTotal(0);
            setStatus('idle');
            return;
        }
        void fetchPage(1, false);
    }, [args.mode, args.open, fetchPage, minChars, search]);

    const onSearchChange = useCallback((value: string) => {
        setSearchInput(value);
        if (timer.current != null) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setSearch(value), SEARCH_DEBOUNCE_MS);
    }, []);

    const toggle = useCallback(
        (item: TargetItem, single: boolean) => {
            if (single) {
                setAllMatching(false);
                setExcluded({});
                setSelected((prev) => (prev[item._id] ? {} : { [item._id]: item }));
                return;
            }
            if (allMatching) {
                setExcluded((prev) => {
                    const next = { ...prev };
                    if (next[item._id]) delete next[item._id];
                    else next[item._id] = true;
                    return next;
                });
                return;
            }
            setSelected((prev) => {
                const next = { ...prev };
                if (next[item._id]) delete next[item._id];
                else next[item._id] = item;
                return next;
            });
        },
        [allMatching]
    );

    const isSelected = useCallback(
        (id: string) => (allMatching ? !excluded[id] : Boolean(selected[id])),
        [allMatching, excluded, selected]
    );

    const selectedCount = allMatching
        ? Math.max(0, total - Object.keys(excluded).length)
        : Object.keys(selected).length;

    const selectAllMatching = useCallback(() => {
        setAllMatching(true);
        setExcluded({});
        setSelected({});
    }, []);

    const clearSelection = useCallback(() => {
        setAllMatching(false);
        setExcluded({});
        setSelected({});
    }, []);

    const selectGroup = useCallback(
        (groupItems: TargetItem[]) => {
            if (allMatching) {
                setExcluded((prev) => {
                    const next = { ...prev };
                    groupItems.forEach((item) => {
                        delete next[item._id];
                    });
                    return next;
                });
                return;
            }
            setSelected((prev) => {
                const next = { ...prev };
                groupItems.forEach((item) => {
                    next[item._id] = item;
                });
                return next;
            });
        },
        [allMatching]
    );

    const loadMore = useCallback(() => {
        if (loadingMore) return;
        void fetchPage(page + 1, true);
    }, [fetchPage, loadingMore, page]);

    const clearFilters = useCallback(() => {
        setSearchInput('');
        setSearch('');
        setStateFilters([]);
        setBrandFilters([]);
        setSelected({});
        setAllMatching(false);
        setExcluded({});
    }, []);

    const loadBrandOptions = useCallback(
        async (query: string) => {
            if (args.level !== 'STORE') return;
            setBrandOptionsStatus('loading');
            try {
                const data = await listStoreBrandTargets({
                    search: query.trim() || undefined,
                    page: 1,
                    limit: 50,
                });
                setBrandOptions(data.items);
                setBrandOptionsStatus('success');
            } catch {
                setBrandOptionsStatus('error');
            }
        },
        [args.level]
    );

    const ensureBrandOptions = useCallback(() => {
        if (brandOptionsStatus === 'idle' || brandOptionsStatus === 'error') {
            void loadBrandOptions(brandOptionSearch);
        }
    }, [brandOptionSearch, brandOptionsStatus, loadBrandOptions]);

    const onBrandOptionQueryChange = useCallback(
        (value: string) => {
            setBrandOptionInput(value);
            if (brandTimer.current != null) window.clearTimeout(brandTimer.current);
            brandTimer.current = window.setTimeout(() => {
                setBrandOptionSearch(value);
                void loadBrandOptions(value);
            }, SEARCH_DEBOUNCE_MS);
        },
        [loadBrandOptions]
    );

    const collectAllMatching = useCallback(async () => {
        const ids: string[] = [];
        let nextPage = 1;
        let fetched = 0;
        let matching = total;
        while (true) {
            const filters = buildFilters({
                search,
                stateFilters,
                brandFilters: args.level === 'STORE' ? brandFilters : [],
                page: nextPage,
                limit: 50,
            });
            const data =
                args.level === 'STORE_BRAND'
                    ? await listStoreBrandTargets(filters)
                    : await listStoreTargets(filters);
            matching = data.total;
            for (const item of data.items) ids.push(item._id);
            fetched += data.items.length;
            if (fetched >= data.total || data.items.length === 0) break;
            nextPage += 1;
        }
        return { ids, total: matching };
    }, [args.level, brandFilters, search, stateFilters, total]);

    const collectApplyIds = useCallback(async () => {
        if (!allMatching) {
            const ids = Object.keys(selected);
            return { ids, total: ids.length };
        }
        const collected = await collectAllMatching();
        const ids = collected.ids.filter((id) => !excluded[id]);
        return { ids, total: ids.length };
    }, [allMatching, collectAllMatching, excluded, selected]);

    const trueEmpty =
        !hasFilters &&
        ((args.mode === 'copy' &&
            catalogStatus === 'success' &&
            catalogTotal === 0 &&
            searchInput.trim().length === 0) ||
            (args.mode === 'apply' && status === 'success' && items.length === 0));
    const filteredEmpty =
        !gated && !trueEmpty && status === 'success' && items.length === 0 && hasFilters;

    return {
        searchInput,
        onSearchChange,
        gated,
        waitingSearch,
        stateFilters,
        setStateFilters,
        brandFilters,
        setBrandFilters,
        items,
        total,
        catalogTotal,
        catalogStatus,
        status,
        error,
        retry: () => {
            if (args.mode === 'copy' && catalogStatus === 'error') void probeCatalog();
            else void fetchPage(1, false);
        },
        loadMore,
        canLoadMore: items.length < total,
        loadingMore,
        moreError,
        selected,
        selectedIds: Object.keys(selected),
        selectedCount,
        allMatching,
        isSelected,
        toggle,
        selectAllMatching,
        clearSelection,
        selectGroup,
        collectAllMatching,
        collectApplyIds,
        clearFilters,
        hasFilters,
        trueEmpty,
        filteredEmpty,
        brandOptions,
        brandOptionsStatus,
        brandOptionInput,
        onBrandOptionQueryChange,
        ensureBrandOptions,
        retryBrandOptions: () => void loadBrandOptions(brandOptionSearch),
    };
}
